import {getNumberFieldValidationMessage, parseNumber} from '@github-ui/memex-field-validators'
import {TextInput} from '@primer/react'
import {type ChangeEvent, type KeyboardEvent, useCallback, useMemo, useRef, useState} from 'react'

import {LABELS} from '../../../constants/labels'
import textStyles from '../../fields/IssueFieldTextToken.module.css'
import type {ProjectFieldNode, ProjectNumberFieldValueNode} from '../project-item-section-fields-query-types'
import {FieldWrapper} from './FieldWrapper'
import type {BaseFieldProps} from './Shared'

export type NumberFieldConfigFragment = Required<Pick<ProjectFieldNode, 'id' | 'name'>>

export type NumberFieldFragment = Pick<ProjectNumberFieldValueNode, 'id' | 'number'>

type NumberFieldProps = BaseFieldProps<NumberFieldConfigFragment, NumberFieldFragment>

export function NumberField({viewerCanUpdate, itemId, field, value, onUpdateFieldValue}: NumberFieldProps) {
  const [showInput, setShowInput] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Text editor is clean while the user hasn't made any changes,
  // prevent navigating away if there are unsaved changes
  const [isClean, setIsClean] = useState<boolean>(true)

  const fieldId = field.id
  const fieldName = field.name

  const originalValue = value?.number?.toString() ?? ''
  const [currentValue, setCurrentValue] = useState<string>(originalValue)
  const [invalidMessage, setInvalidMessage] = useState<string | undefined>(undefined)
  const [shouldRestoreRowFocus, setShouldRestoreRowFocus] = useState(false)

  const commitCurrentValue = useCallback(() => {
    if (invalidMessage) {
      setCurrentValue(originalValue)
      setInvalidMessage(undefined)
    } else {
      const newValue = currentValue === '' ? null : parseNumber(currentValue)
      onUpdateFieldValue(fieldId, {
        id: `optimistic-field-value-${itemId}-${fieldId}`,
        field: {id: fieldId},
        number: newValue,
      })
    }
  }, [currentValue, fieldId, invalidMessage, itemId, onUpdateFieldValue, originalValue])

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setInvalidMessage(getNumberFieldValidationMessage(newValue))
      setCurrentValue(newValue)
      if (newValue === originalValue) {
        setIsClean(true)
      } else {
        setIsClean(false)
      }
    },
    [originalValue],
  )

  const onUpdate = useCallback(() => {
    commitCurrentValue()
    setShowInput(false)
    setIsClean(true)
    setShouldRestoreRowFocus(true)
  }, [commitCurrentValue])

  const onCancel = useCallback(() => {
    if (inputRef.current) {
      setCurrentValue(originalValue)
      // eslint-disable-next-line github/no-blur
      inputRef.current.blur()
      setShowInput(false)
      setIsClean(true)
      setShouldRestoreRowFocus(true)
    }
  }, [originalValue])

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Enter') {
        // We need to stop this as the key events sometimes are handled outside React’s normal event pipeline,
        // so the component doesn’t re-render in time, and we cannot restore the focus to the button. This doesn't
        // have any negative side effects as we handle form submission carefully.
        e.preventDefault()
        onUpdate()
      }
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCancel()
      }
    },
    [onCancel, onUpdate],
  )

  const preventSubmit = useMemo<boolean>(() => !!invalidMessage, [invalidMessage])

  if (!viewerCanUpdate)
    return (
      <FieldWrapper
        showInput={showInput}
        setShowInput={setShowInput}
        placeholder={LABELS.emptySections.noValue(fieldName)}
        canUpdate={false}
        value={currentValue}
        name={fieldName}
      />
    )

  return (
    <FieldWrapper
      showInput={showInput}
      setShowInput={setShowInput}
      value={currentValue}
      name={fieldName}
      placeholder={LABELS.emptySections.numberPlaceholder}
      onCommit={commitCurrentValue}
      validationMessage={invalidMessage}
      preventSubmit={preventSubmit}
      isClean={isClean}
      setIsClean={setIsClean}
      shouldRestoreRowFocus={shouldRestoreRowFocus}
      setShouldRestoreRowFocus={setShouldRestoreRowFocus}
      input={
        <TextInput
          data-testid="number-field-input"
          placeholder={LABELS.emptySections.numberPlaceholder}
          ref={inputRef}
          size="small"
          onChange={onChange}
          onKeyDown={onKeyDown}
          value={currentValue}
          className={textStyles.issueFieldTextInput}
          onFocus={event => event.target.setSelectionRange(event.target.value.length, event.target.value.length)}
          inputMode="numeric"
          autoFocus
        />
      }
    />
  )
}
